import { useState } from "react";
import {
  FiCheckSquare,
  FiSquare,
  FiShoppingCart,
  FiCheck,
  FiX,
} from "react-icons/fi";
import { getMatchMeta } from "../../../Screens/User/Quotations";
import { assets } from "../../../Assets/assets";

const QuotationCard = ({
  quotation,
  isChecked,
  onCheck,
  onSelect,
  onAddToCart,
  onRemoveFromCart,
  isSelected,
  isBestMatch,
  order,
  onViewDetails,
  isInCart,
  isPaidInCart,
  isSampleSelected,
  isFullOrderSelected,
  cartLoading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const pct = quotation.matchingPercentage;
  const meta = pct !== undefined ? getMatchMeta(pct) : null;

  // ── Derived state ──────────────────────────────────────────────────────────
  const isAnySelected = isSampleSelected || isFullOrderSelected;

  // ── Footer button rendering ────────────────────────────────────────────────
  const renderCartButton = () => {
    if (isFullOrderSelected) {
      return (
        <div className="flex items-center justify-center gap-1.5 w-full py-2 bg-orange-50 border border-orange-200 rounded-lg">
          <FiCheck className="text-orange-600 text-xs" />
          <span className="text-xs font-semibold text-orange-600">Full Order</span>
        </div>
      );
    }

    if (isSampleSelected) {
      return (
        <div className="flex items-center justify-center gap-1.5 w-full py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <FiCheck className="text-emerald-600 text-xs" />
          <span className="text-xs font-semibold text-emerald-600">{isPaidInCart ? "Paid" : "Selected"}</span>
        </div>
      );
    }

    if (isInCart) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); onRemoveFromCart(quotation); }}
          disabled={cartLoading}
          className="flex items-center justify-center gap-1.5 w-full py-2 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
        >
          {cartLoading ? (
            <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <FiX className="text-red-600 text-xs" />
          )}
          <span className="text-xs font-semibold text-red-600">Remove</span>
        </button>
      );
    }

    return (
      <button
        onClick={(e) => { e.stopPropagation(); onAddToCart(quotation); }}
        disabled={cartLoading}
        className="flex items-center justify-center gap-1.5 w-full py-2 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
      >
        {cartLoading ? (
          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <FiShoppingCart className="text-xs hidden sm:block" />
        )}
        Add to Cart
      </button>
    );
  };

  return (
    <div
      style={{ backgroundImage: `url(${assets.frame})` }}
      className={`rounded-xl border-2 transition-all flex flex-col bg-top bg-cover bg-no-repeat ${isInCart
        ? "border-primaryColor shadow-md shadow-primaryColor/10"
        : isSampleSelected
          ? "border-emerald-400 shadow-md shadow-emerald-100"
          : isFullOrderSelected
            ? "border-orange-400 shadow-md shadow-orange-100"
            : isChecked
              ? "border-primaryColor shadow-md"
              : "border-gray-200 hover:border-gray-300"
        }`}
    >
      {/* Best Match Banner */}
      {isBestMatch && (
        <div className="text-primaryColor text-xs font-bold text-center py-1 rounded-t-xl tracking-wide">
          ⭐ BEST MATCH
        </div>
      )}

      {/* In-Cart Banner */}
      {isInCart && !isSampleSelected && !isFullOrderSelected && (
        <div className="bg-primaryColor/10 text-primaryColor text-[10px] font-bold text-center py-1 rounded-t-xl tracking-wide flex items-center justify-center gap-1">
          <FiShoppingCart className="text-[10px]" /> In Sample Cart
        </div>
      )}

      {/* Header: Checkbox + ID + Match % */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-start flex-wrap gap-2">
          <div className="flex items-start gap-2">
            <button
              onClick={onCheck}
              title="Add to compare"
              className="flex-shrink-0 mt-0.5"
            >
              {isChecked ? (
                <FiCheckSquare className="w-5 h-5 text-primaryColor" />
              ) : (
                <FiSquare className="w-5 h-5 text-gray-300" />
              )}
            </button>

            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm text-gray-900 truncate">
                #{quotation.quotationUniqueNumber}
              </h3>
              <p className="text-xs text-gray-400">
                {new Date(quotation.createdAt).toLocaleDateString("en-GB")}
              </p>
            </div>
          </div>

          {meta && (
            <div
              className={`flex-shrink-0 w-full flex flex-col items-center px-2 py-1 rounded-lg border ${meta.bg} ${meta.border}`}
            >
              <span className={`text-xs font-black leading-none ${meta.text}`}>
                {pct}%
              </span>
              <span className={`text-[9px] font-semibold ${meta.text}`}>
                {meta.label}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2 flex-1">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xs text-gray-400 mb-0.5">
              Per {order?.priceAndQuantity?.standardizedUnit || "Unit"}
            </p>
            <p className="text-sm font-bold text-primaryColor">
              {quotation.currency} {quotation.pricing?.commissionAdjustedAmount || 0}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xs text-gray-400 mb-0.5">Quantity</p>
            <p className="text-sm font-semibold text-gray-900">
              {quotation.quantity?.isAvailableFull ? (
                <span className="text-green-600 text-xs">Full Avail.</span>
              ) : (
                `${quotation.quantity?.volume || 0} ${quotation.standardizedUnit || ""}`
              )}
            </p>
          </div>
        </div>

        <div className="text-xs text-gray-500">
          <span className="font-medium text-gray-600">Hold: </span>
          {quotation.holdingPeriod?.from && quotation.holdingPeriod?.to ? (
            <>
              {new Date(quotation.holdingPeriod.from).toLocaleDateString("en-GB")}{" "}
              –{" "}
              {new Date(quotation.holdingPeriod.to).toLocaleDateString("en-GB")}
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold text-[10px]">
                {Math.ceil(
                  (new Date(quotation.holdingPeriod.to) -
                    new Date(quotation.holdingPeriod.from)) /
                  (1000 * 60 * 60 * 24)
                )}{" "}
                days
              </span>
            </>
          ) : (
            "Not specified"
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 pb-3 grid grid-cols-2 gap-2">
        {quotation.specification && (
          <button
            onClick={onViewDetails}
            className="w-full py-2 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg text-xs font-semibold transition-colors"
          >
            View Details
          </button>
        )}
        {renderCartButton()}
      </div>
    </div>
  );
};

export default QuotationCard;















// import React, { useState } from 'react'
// import { FiCheckSquare, FiSquare } from 'react-icons/fi';
// import { getMatchMeta } from '../../../Screens/User/Quotations';
// import { assets } from '../../../Assets/assets';

// const QuotationCard = ({ quotation, isChecked, onCheck, onSelect, isSelected, isBestMatch, order, onViewDetails }) => {
//   const [expanded, setExpanded] = useState(false);
//   const pct = quotation.matchingPercentage;
//   const meta = pct !== undefined ? getMatchMeta(pct) : null;

//   return (
//     <div style={{ backgroundImage: `url(${assets.frame})` }} className={`rounded-xl border-2 transition-all flex flex-col bg-top bg-cover bg-no-repeat ${isChecked ? "border-primaryColor shadow-md" : "border-gray-200 hover:border-gray-300"
//       }`}>

//       {/* Best Match Banner */}
//       {isBestMatch && (
//         <div className="text-primaryColor text-xs font-bold text-center py-1 rounded-t-xl tracking-wide">
//           ⭐ BEST MATCH
//         </div>
//       )}

//       {/* Header: Checkbox + ID + Match % */}
//       <div className="p-3 border-b border-gray-100">
//         <div className="flex items-start gap-2">
//           <button onClick={onCheck} title="Add to compare" className="flex-shrink-0 mt-0.5">
//             {isChecked
//               ? <FiCheckSquare className="w-5 h-5 text-primaryColor" />
//               : <FiSquare className="w-5 h-5 text-gray-300" />
//             }
//           </button>

//           <div className="flex-1 min-w-0">
//             <h3 className="font-bold text-sm text-gray-900 truncate">
//               #{quotation.quotationUniqueNumber}
//             </h3>
//             <p className="text-xs text-gray-400">
//               {new Date(quotation.createdAt).toLocaleDateString("en-GB")}
//             </p>
//           </div>

//           {/* Matching % badge */}
//           {meta && (
//             <div className={`flex-shrink-0 flex flex-col items-center px-2 py-1 rounded-lg border ${meta.bg} ${meta.border}`}>
//               <span className={`text-xs font-black leading-none ${meta.text}`}>{pct}%</span>
//               <span className={`text-[9px] font-semibold ${meta.text}`}>{meta.label}</span>
//             </div>
//           )}
//         </div>

//       </div>

//       {/* Body */}
//       <div className="p-3 space-y-2 flex-1">
//         {/* Price & Quantity */}
//         <div className="grid grid-cols-2 gap-2">
//           <div className="bg-gray-50 rounded-lg p-2">
//             <p className="text-xs text-gray-400 mb-0.5">Per {order?.priceAndQuantity?.standardizedUnit || "Unit"}</p>
//             <p className="text-sm font-bold text-primaryColor">{quotation.currency} {quotation.pricing?.amount || 0}</p>
//           </div>
//           <div className="bg-gray-50 rounded-lg p-2">
//             <p className="text-xs text-gray-400 mb-0.5">Quantity</p>
//             <p className="text-sm font-semibold text-gray-900">
//               {quotation.quantity?.isAvailableFull
//                 ? <span className="text-green-600 text-xs">Full Avail.</span>
//                 : `${quotation.quantity?.volume || 0} ${quotation.standardizedUnit || ""}`
//               }
//             </p>
//           </div>
//         </div>

//         {/* Holding Period */}
//         <div className="text-xs text-gray-500">
//           <span className="font-medium text-gray-600">Hold: </span>

//           {quotation.holdingPeriod?.from && quotation.holdingPeriod?.to ? (
//             <>
//               {new Date(quotation.holdingPeriod.from).toLocaleDateString("en-GB")} –{" "}
//               {new Date(quotation.holdingPeriod.to).toLocaleDateString("en-GB")}

//               <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold text-[10px]">
//                 {Math.ceil(
//                   (new Date(quotation.holdingPeriod.to) -
//                     new Date(quotation.holdingPeriod.from)) /
//                   (1000 * 60 * 60 * 24)
//                 )}{" "}
//                 days
//               </span>
//             </>
//           ) : (
//             "Not specified"
//           )}
//         </div>


//       </div>

//       {/* Footer: Select Quotation (proceed to sample/full order) */}
//       <div className="px-3 pb-3 grid grid-cols-2 gap-2">
//         {/* Expandable Details */}
//         {quotation.specification && (
//           <>
//             <button
//               onClick={onViewDetails}
//               className="w-full py-2 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg text-xs font-semibold transition-colors"
//             >
//               View Details
//             </button>
//           </>
//         )}
//         <button
//           onClick={onSelect}
//           className="w-full py-2 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-lg text-xs font-semibold transition-colors"
//         >
//           Select Quotation
//         </button>
//       </div>
//     </div>
//   );
// };

// export default QuotationCard